import { FlowClient, TaskClient, GuidStringKeyValuePair, FlowRoleInputDto, AttachmentDataInputDto, AttachmentsClient, SaveFlowInputDto, CommentScopeOutputDto } from './../../../core/remote-clients/remote-client';
import { AfterViewInit, Component, Inject, Injector } from '@angular/core';
import { ItemScope, QformFlowInfo } from 'qflow-form-renderer';
import { FormType } from '../../../models/form-type.model';

import {
  API_BASE_URL,
  DataDomainClient,
  FlowDataOutputDto,
  FlowRenderInfoOutputDto,
  FlowRoleOutputDto,
  FormsClient,
  RoleClient,
  SessionClient,
} from '../../../core/remote-clients/remote-client';
import { RedirectService } from '../../../core/services/redirect.service';
import { SessionService } from '../../../core/services/session.service';
import { ComponentDataService } from '../../../services/component-data.service';
import { FlowFormScopesResult, FlowFormScopesService } from '../../../services/remote/flow-form-scopes.service';
import { ComponentData } from '../../../models/component-data.model';
import { RemoteDataDomainService } from '../../../services/remote/remote-data-domain.service';
import { RemoteFlowService } from '../../../services/remote/remote-flow.service';
import { RemoteRoleService } from '../../../services/remote/remote-role.service';
import { RemoteServiceBase } from '../../../services/remote/remote-base.service';
import { FormRenderComponent } from '../form-render/form-render.component';
import { InteractiveStepBaseComponent } from '../interactive-step-base/interactive-step-base.component';
import { ButtonsContainerComponent, DropdownItem, FormHeaderComponent, QformAttachmentRequestEvent, QformEvent, QformEventBus } from 'qflow-form-renderer';
import { AttachmentService } from '../../../services/attachments/attachment.service';
import { ConfigurationService } from '../../../services/configuration.service.ts/configuration.service';

interface FlowInfoEditData {
  flowName?: string;
  flowDescription?: string;
  importance?: string;
  flag?: string;
  progress?: number;
}

@Component({
  selector: 'app-flow-form',
  imports: [FormRenderComponent, ButtonsContainerComponent, FormHeaderComponent],
  providers: [
    ComponentDataService,
    SessionClient,
    SessionService,
    RedirectService,
    RemoteServiceBase,
    RemoteFlowService,
    RemoteRoleService,
    RemoteDataDomainService,
    RoleClient,
    DataDomainClient,
    FormsClient,
    FlowFormScopesService,
    FlowClient,
    TaskClient,
    AttachmentsClient,
    AttachmentService
  ],
  templateUrl: './flow-form.component.html',
  styleUrl: './flow-form.component.scss',
})
export class FlowFormComponent
  extends InteractiveStepBaseComponent
  implements AfterViewInit
{
  flowId: string = '';
  flowName: string = '';
  flowCorrelativeId: number | undefined;
  isReadOnly: boolean = true;

  flowData: FlowDataOutputDto[] = [];
  flowRoles: FlowRoleOutputDto[] = [];
  titleMessageScopes: Record<string, ItemScope> = {};
  isEditForm: boolean = false;
  headerTitle: string = '';
  headerDropdownUrls: DropdownItem[] | undefined = undefined;

  private readonly _apiBaseUrl: string;

  attachments?: AttachmentDataInputDto[] = [];

  constructor(
    injector: Injector,
    private readonly _flowFormScopes: FlowFormScopesService,
    private readonly _attachmentService: AttachmentService,
    private readonly _config: ConfigurationService,
    private readonly _eventBus: QformEventBus,
    @Inject(API_BASE_URL) apiBaseUrl: string
  ) {
    super(injector);
    this._apiBaseUrl = apiBaseUrl;
    this.flowId = this._route.snapshot.queryParamMap.get('flowId') ?? '';
    const scopeMode = this._route.snapshot.queryParamMap.get('scopeMode') ??
      this._route.snapshot.data['defaultScopeMode'] ?? '';
    const normalizedScopeMode = scopeMode.toString().trim().toLowerCase();
    this.isEditForm = normalizedScopeMode === FormType.EditForm;
    this.headerTitle = this._localizationService.localize(
      this.isEditForm ? 'EditFlow' : 'ViewFlow'
    );

    this._eventBus.listen(
      QformEvent.AttachmentDownloadRequested,
      async (args: QformAttachmentRequestEvent) => {
        this._attachmentService.downloadFlowAttachment(
          args.attachId,
          args.version
        );
      }
    );

    this._eventBus.listen(
      QformEvent.AttachmentDeletionRequested,
      async (args: QformAttachmentRequestEvent) => {
        this._attachmentService.deleteFlowAttachment(
          this.flowId,
          '',
          '',
          args.attachId,
          args.version
        );
      }
    );
  }

  async ngAfterViewInit(): Promise<void> {
    this.showLoader();

    if (!this.isValidGuid(this.flowId)) {
      this.redirectToErrorPage(this._localizationService.localize('FlowNotFound'));
      this.hideLoader();
      return;
    }

    try {
      const formType = this.getNormalizedScopeType() === FormType.EditForm ? 1 : 0;
      const info: FlowRenderInfoOutputDto = await this._flowService.getFlowRenderInfo(this.flowId, formType);
      if (!info) {
        this.redirectToErrorPage(
          this._localizationService.localize('UnexpectedErrorLoadingFlowForm')
        );
        return;
      }

      this.flowName = info.flowName ?? '';
      this.flowCorrelativeId = info.flowCorrelativeId;
      this.versionId = info.versionId ?? '';
      this.isReadOnly = this.getIsReadOnly(info.isReadOnly ?? false);
      this.flowData = info.flowDatas ?? [];
      this.flowRoles = info.flowRoles ?? [];
      this.commentsScope = this.normalizeCommentScope(info.scope?.commentScope);
      this.attachmentsScope = info.scope?.attachmentsScope;

      const formJson = JSON.parse(info.customForm?.content ?? '');
      this.qformCard.parse(formJson);
      this.initDependencies(formJson, this.flowData);

      await this.applyFlowFormsScopesIfNeeded(formJson);

      this.initData(this.flowData);
      await this.fillListDataOptions(this.flowData);
      await this.fillDataControlWithValueAndScope(this.flowData);
      await this.fillDependentListDataOptions(this.flowData);
      this.fillFileDataWithScope();
      this.fillRolesWithScope(this.flowRoles);

      if (this.qformCard.getCommentLog()) {
        this.initCommentLog(info.comments ?? []);
      }

      this.initFlowInfo({
        correlativeId: info.flowCorrelativeId,
        name: info.flowName,
        description: info.flowDescription,
        initiatorUser: info.initiatorUserName,
        startDate: info.startDate,
        status: info.status,
        importance: info.importance,
        flag: info.flag,
        progress: info.progress,
      });

      this.qformCard.getFlowInfoItems().forEach((item: QformFlowInfo) => {
        const scope = item.id ? this.titleMessageScopes[this.normalizeId(item.id)] : undefined;
        if (scope !== undefined) {
          item.scope = scope;
        }
      });

      this.renderQformCard();
      this.updateTitle(this.flowName);
      this.setHeaderDropdownUrls();
    } catch {
      this.redirectToErrorPage(
        this._localizationService.localize('UnexpectedErrorLoadingFlowForm')
      );
    } finally {
      this.hideLoader();
    }
  }

  private setHeaderDropdownUrls(): void {
    const flowDetailsRouteTemplate = this._config.get<string>('FlowDetailsRoute');
    const viewFlowRouteTemplate = this._config.get<string>('ViewFlowRoute');

    const flowDetailsRoute = flowDetailsRouteTemplate
      ? this.buildRouteWithTenant(flowDetailsRouteTemplate, this.flowId)
      : undefined;

    const viewFlowRoute = viewFlowRouteTemplate
      ? this.buildRouteWithTenant(viewFlowRouteTemplate, this.flowId)
      : undefined;

    const editFlowRoute = viewFlowRouteTemplate
      ? this.buildEditRouteFromView(viewFlowRouteTemplate, this.flowId)
      : undefined;

    const flowDetailsItem: DropdownItem | undefined = flowDetailsRoute
      ? {
          label: this._localizationService.localize('FlowDetails'),
          url: flowDetailsRoute,
        }
      : undefined;

    if (this.isEditForm) {
      this.headerDropdownUrls = [
        ...(flowDetailsItem ? [flowDetailsItem] : []),
        ...(viewFlowRoute
          ? [
              {
                label: this._localizationService.localize('ViewFlow'),
                url: viewFlowRoute,
              } as DropdownItem,
            ]
          : []),
      ];
    } else {
      this.headerDropdownUrls = [
        ...(flowDetailsItem ? [flowDetailsItem] : []),
        ...(editFlowRoute
          ? [
              {
                label: this._localizationService.localize('EditFlow'),
                url: editFlowRoute,
              } as DropdownItem,
            ]
          : []),
      ];
    }
  }

  private buildRouteWithTenant(template: string, flowId: string): string {
    const filled = template.replace('{0}', flowId);
    const [baseRoute, queryString] = filled.split('?');
    const params = new URLSearchParams(queryString ?? '');
    params.set('flowId', flowId);
    params.set('tenantId', this.tenantId);
    return `${baseRoute}?${params.toString()}`;
  }

  private normalizeId(id: string | undefined | null): string {
    return (id ?? '').toString().toLowerCase();
  }

  private applyScopeDictionary(scopesById: Record<string, ItemScope>): void {
    for (const [rawId, altScope] of Object.entries(scopesById)) {
      const id = this.normalizeId(rawId);
      const scope = this.normalizeScope(altScope);
      if (scope === undefined) {
        continue;
      }
      const data = (this.flowData ?? []).find(d => this.normalizeId(d.id) === id);
      if (data) {
        this.setDtoScope(data, scope);
        continue;
      }

      const role = (this.flowRoles ?? []).find(r => this.normalizeId(r.id) === id);
      if (role) {
        this.setDtoScope(role, scope);
        continue;
      }

      this.titleMessageScopes[id] = scope;
    }
  }

  private setDtoScope(target: { scope?: unknown }, scope: ItemScope): void {
    target.scope = this.toNumericScopeValue(scope);
  }

  private toNumericScopeValue(scope: ItemScope): number {
    switch (scope) {
      case ItemScope.Missing:
        return 0;
      case ItemScope.Hidden:
        return 1;
      case ItemScope.ReadOnly:
        return 2;
      case ItemScope.Editable:
        return 3;
      case ItemScope.Required:
        return 4;
      default:
        return 0;
    }
  }

  private applyCommentsScopeFromFlowFormScopes(scopes: FlowFormScopesResult): void {
    const hasCommentFlags =
      scopes.allowAdition !== undefined ||
      scopes.viewHistory !== undefined ||
      scopes.required !== undefined;

    if (!hasCommentFlags) {
      return;
    }

    const normalized = this.normalizeCommentScope({
      allowAdition: scopes.allowAdition,
      allowAddition: scopes.allowAdition,
      viewHistory: scopes.viewHistory,
      required: scopes.required
    });

    if (normalized) {
      this.commentsScope = normalized;
    }
  }

  private applyTitleMessageScopesFromContent(
    formJson: any,
    type: FormType
  ): void {
    const flowFormScopes = Array.isArray(formJson?.flowFormScopes)
      ? formJson.flowFormScopes
      : undefined;
    if (!flowFormScopes?.length) {
      return;
    }

  const selectedField = type === FormType.EditForm ? 'editForm' : FormType.Form;
    const scopesById: Record<string, ItemScope> = {};

    for (const entry of flowFormScopes) {
      const id = this.normalizeId(entry?.dataId);
      if (!id) {
        continue;
      }

      const rawScope =
        entry?.[selectedField] ??
        (selectedField === 'editForm' ? entry?.form : entry?.editForm);
      const scope = this.normalizeScope(rawScope);
      if (scope !== undefined) {
        scopesById[id] = scope;
      }
    }

    if (!Object.keys(scopesById).length) {
      return;
    }

    for (const [id, scope] of Object.entries(scopesById)) {
      this.titleMessageScopes[id] = scope;
    }
  }

  private getNormalizedScopeType(): FormType | undefined {
    const scopeMode = this._route.snapshot.queryParamMap.get('scopeMode') ??
      this._route.snapshot.data['defaultScopeMode'] ?? '';
    const rawType = (scopeMode || this._route.snapshot.queryParamMap.get('type') || '').toString();
    const normalized = rawType.toLowerCase();
    switch (normalized) {
      case FormType.Form:
        return FormType.Form;
      case FormType.EditForm:
        return FormType.EditForm;
      default:
        return undefined;
    }
  }

  private async applyFlowFormsScopesIfNeeded(formJson: any): Promise<void> {
    const type = this.getNormalizedScopeType();
    if (!type) {
      return;
    }

    this.applyTitleMessageScopesFromContent(formJson, type);

    const packageId = this._route.snapshot.queryParamMap.get('packageId') ?? '';
    if (!packageId) {
      return;
    }
    try {
      const scopes = await this._flowFormScopes.getFlowFormsScope(
        packageId,
        type
      );

      this.applyScopeDictionary(scopes.dataScopes);
      this.applyCommentsScopeFromFlowFormScopes(scopes);
    } catch {
      return;
    }
  }

  private getIsReadOnly(backendReadOnly: boolean): boolean {
    const type = this.getNormalizedScopeType();
  return type === FormType.EditForm ? false : backendReadOnly;
  }

  async saveFlowForm(): Promise<void> {
    try {
      this.validateFlowInfoEditableFields();
      this.validateForm(this.commentsScope?.required ?? false);

      this.showLoader();

      await this.uploadFiles();

      const dataValues = this.getDataValues();
      const roleMembers = this.getRoleMembers();

      const saveFlowDto = this.buildSaveFlowDto(dataValues, roleMembers);

      await this._flowService.saveFlow(saveFlowDto as SaveFlowInputDto);

      if (this.isEditForm) {
        this._redirectService.redirectToViewFlow(this.flowId);
      }

      this.hideLoader();
    } catch (error) {
      this.handleException(error, 'UnexpectedErrorSavingFlowForm');
      this.hideLoader();
    } finally {
      this.attachments = [];
    }
  }

  private async uploadFiles() {
    this.attachments = await this._attachmentService.uploadTempFiles(
      this.qformCard,
      this.versionId
    );
  }

  private buildSaveFlowDto(
    dataValues: GuidStringKeyValuePair[],
    roleMembers: FlowRoleInputDto[]
  ): SaveFlowInputDto {
    const comment = this.getComment() ?? '';
    const flowInfoEdit = this.getFlowInfoEditData();

    const dto = new SaveFlowInputDto();
    dto.flowId = this.flowId;
    dto.dataIdsWithValues = dataValues;
    dto.roles = roleMembers;
    dto.attachments = this.attachments;
    dto.comment = comment;
    dto.versionId = this.versionId;

    if (flowInfoEdit) {
      dto.flowName = flowInfoEdit.flowName;
      dto.flowDescription = flowInfoEdit.flowDescription;
      dto.importance = flowInfoEdit.importance;
      dto.flag = flowInfoEdit.flag;
      dto.progress = flowInfoEdit.progress;
    }

    return dto;
  }

  private validateFlowInfoEditableFields(): void {
    const editableItem = this.qformCard.getFlowInfoItems().find(
      (item: QformFlowInfo) => item.scope === ItemScope.Editable
    );
    if (!editableItem) return;

    if (!editableItem.flowName?.trim()) {
      throw new Error(this._localizationService.localize('FlowNameRequired'));
    }

    if (editableItem.progress == null) {
      throw new Error(this._localizationService.localize('FlowProgressRequired'));
    }
  }

  private getFlowInfoEditData(): FlowInfoEditData | undefined {
    const editableItem = this.qformCard.getFlowInfoItems().find(
      (item: QformFlowInfo) => item.scope === ItemScope.Editable
    );
    if (!editableItem) {
      return undefined;
    }
    return {
      flowName: editableItem.flowName,
      flowDescription: editableItem.description,
      importance: editableItem.importanceKey,
      flag: editableItem.flag,
      progress: editableItem.progress,
    };
  }

  private buildEditRouteFromView(viewTemplate: string, flowId: string): string | undefined {
    const filled = viewTemplate.replace('{0}', flowId);
    const [baseRoute, queryString] = filled.split('?');
    const editBase = baseRoute.replace(/Display/i, 'Edit');
    if (!editBase) return undefined;
    const params = new URLSearchParams(queryString ?? '');
    params.set('flowId', flowId);
    params.set('tenantId', this.tenantId);
    params.set('scopeMode', FormType.EditForm);
    return `${editBase}?${params.toString()}`;
  }

  protected override getCurrentStepId(): string | undefined {
    return undefined;
  }

  protected override getComponentScope(
    component: ComponentData
  ): ItemScope | undefined {
    const scope = component.id ? this.titleMessageScopes[this.normalizeId(component.id)] : undefined;
    return scope;
  }

  private normalizeScope(rawScope: any): ItemScope | undefined {
    if (rawScope === null || rawScope === undefined) {
      return undefined;
    }
    const isDataOrRole = typeof rawScope === 'number';
    const isTitleOrMessage = typeof rawScope === 'string';

    if (isDataOrRole) {
      switch (rawScope) {
        case 0:
          return ItemScope.Missing;
        case 1:
          return ItemScope.Hidden;
        case 2:
          return ItemScope.ReadOnly;
        case 3:
          return ItemScope.Editable;
        case 4:
          return ItemScope.Required;
        default:
          return undefined;
      }
    }

    if (isTitleOrMessage) {
      const normalized = rawScope.toLowerCase();
      switch (normalized) {
        case 'missing':
          return ItemScope.Missing;
        case 'hidden':
          return ItemScope.Hidden;
        case 'readonly':
          return ItemScope.ReadOnly;
        case 'editable':
          return ItemScope.Editable;
        case 'required':
          return ItemScope.Required;
        default: {
          const parsed = Number(rawScope);
          return Number.isNaN(parsed) ? undefined : (parsed as ItemScope);
        }
      }
    }

    return undefined;
  }

  private normalizeCommentScope(raw: any): CommentScopeOutputDto | undefined {
    if (!raw) return raw;

    const allowAddition =
      raw.allowAddition ?? raw.allowAdition ?? raw.addAllowed ?? false;
    const viewCommentHistory =
      raw.viewCommentHistory ?? raw.viewHistory ?? raw.visible ?? false;
    const required = raw.required ?? false;

    const normalized = new CommentScopeOutputDto();
    normalized.allowAddition = !!allowAddition;
    normalized.viewCommentHistory = !!viewCommentHistory;
    normalized.required = !!required;
    return normalized;
  }
}

